import React, { useEffect, useState } from "react";
import { Volume2, Smartphone, Play } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    loadAlertPrefs,
    saveAlertPrefs,
    vibrationSupported,
    playAlertSound,
    vibrate,
} from "@/lib/tradeAlerts";

const AlertRow = ({ icon: Icon, label, checked, onToggle, disabled, testId, hint }) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
        <div className="flex items-center gap-2">
            <Icon size={13} className={disabled ? "text-[#3A4759]" : "text-[#A7B4C6]"} />
            <div className="leading-tight">
                <div className={`text-[12px] ${disabled ? "text-[#6F7D92]" : "text-[#E6EDF6]"}`}>{label}</div>
                {hint && <div className="text-[10px] text-[#6F7D92]">{hint}</div>}
            </div>
        </div>
        <Switch checked={checked} onCheckedChange={onToggle} disabled={disabled} data-testid={testId} />
    </div>
);

/**
 * Buy/sell alert settings: separate sound + vibration toggles per side.
 *
 * @param onChange(prefs)  called whenever prefs change so a parent can update
 *                         its live ref (alerts take effect immediately).
 */
export const AlertSettingsSection = ({ onChange }) => {
    const [prefs, setPrefs] = useState(loadAlertPrefs());
    const hasVibration = vibrationSupported();

    useEffect(() => {
        onChange?.(prefs);
    }, [onChange, prefs]);

    const update = (patch) => {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        saveAlertPrefs(next);
    };

    return (
        <div
            className="rounded-md p-3 mt-2"
            style={{ background: "rgba(46,230,214,0.05)", border: "1px solid rgba(46,230,214,0.25)" }}
            data-testid="alert-settings-panel"
        >
            <div className="text-[12px] font-semibold text-[#E6EDF6]">Buy / Sell Alerts</div>
            <div className="text-[10px] text-[#A7B4C6] mt-0.5 mb-2">
                Play a sound and/or vibrate when an order fills. Separate for buys and sells.
                {!hasVibration && " Vibration is only available on supported mobile devices."}
            </div>

            <div className="grid grid-cols-2 gap-x-6">
                {/* BUY column */}
                <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#2BE38B] mb-0.5">On BUY fill</div>
                    <AlertRow
                        icon={Volume2} label="Sound"
                        checked={prefs.soundBuy}
                        onToggle={(v) => update({ soundBuy: Boolean(v) })}
                        testId="alert-sound-buy"
                    />
                    <AlertRow
                        icon={Smartphone} label="Vibrate"
                        checked={prefs.vibrateBuy}
                        onToggle={(v) => update({ vibrateBuy: Boolean(v) })}
                        disabled={!hasVibration}
                        testId="alert-vibrate-buy"
                    />
                </div>
                {/* SELL column */}
                <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#FF5C6C] mb-0.5">On SELL fill</div>
                    <AlertRow
                        icon={Volume2} label="Sound"
                        checked={prefs.soundSell}
                        onToggle={(v) => update({ soundSell: Boolean(v) })}
                        testId="alert-sound-sell"
                    />
                    <AlertRow
                        icon={Smartphone} label="Vibrate"
                        checked={prefs.vibrateSell}
                        onToggle={(v) => update({ vibrateSell: Boolean(v) })}
                        disabled={!hasVibration}
                        testId="alert-vibrate-sell"
                    />
                </div>
            </div>

            {/* Volume + test */}
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-[#13202D]">
                <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Volume</Label>
                <input
                    type="range" min="0" max="1" step="0.05"
                    value={prefs.volume}
                    onChange={(e) => update({ volume: Number(e.target.value) })}
                    className="flex-1 accent-[#2EE6D6]"
                    data-testid="alert-volume"
                />
                <button
                    type="button"
                    onClick={() => { playAlertSound("BUY", prefs.volume); if (hasVibration) vibrate("BUY"); }}
                    className="btn-anim inline-flex items-center gap-1 px-2 py-1 rounded border border-[#2BE38B] text-[#2BE38B] text-[10px] hover:bg-[rgba(43,227,139,0.1)]"
                    data-testid="alert-test-buy"
                >
                    <Play size={10} /> Buy
                </button>
                <button
                    type="button"
                    onClick={() => { playAlertSound("SELL", prefs.volume); if (hasVibration) vibrate("SELL"); }}
                    className="btn-anim inline-flex items-center gap-1 px-2 py-1 rounded border border-[#FF5C6C] text-[#FF5C6C] text-[10px] hover:bg-[rgba(255,92,108,0.1)]"
                    data-testid="alert-test-sell"
                >
                    <Play size={10} /> Sell
                </button>
            </div>
        </div>
    );
};

export default AlertSettingsSection;
